'use client';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';

import {
  Button,
  ComponentErrorBoundary,
  LeaderboardFilters,
  LeaderboardMobileCard,
  LeaderboardMobileCardSkeleton,
  LeaderboardTableHeader,
  LeaderboardTableRow,
  LeaderboardTableSkeleton,
  LeaderboardToolbar,
} from '@/components';
import { useT } from '@/hooks';
import { LeaderboardTrader, TimeFilter } from '@/types';

interface LeaderboardTableProps {
  categories: string[];
  timePeriods: string[];
  filteredLeaderboard: LeaderboardTrader[];
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  selectedTimeFilter: TimeFilter;
  setSelectedTimeFilter: (val: TimeFilter) => void;
  selectedCategory: string;
  setSelectedCategory: (val: string) => void;
  orderBy: 'PNL' | 'VOL';
  setOrderBy: (val: 'PNL' | 'VOL') => void;
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}

export const LeaderboardTable = ({
  categories,
  timePeriods,
  filteredLeaderboard,
  searchQuery,
  setSearchQuery,
  selectedTimeFilter,
  setSelectedTimeFilter,
  selectedCategory,
  setSelectedCategory,
  orderBy,
  setOrderBy,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: LeaderboardTableProps) => {
  const { t } = useT();

  const renderLeaderboardRow = (entry: LeaderboardTrader, index: number) => (
    <LeaderboardTableRow
      key={entry.id || index}
      entry={entry}
      index={index}
      orderBy={orderBy}
      category={selectedCategory}
    />
  );

  const renderedEntriesList = filteredLeaderboard?.map(renderLeaderboardRow);
  const hasData = filteredLeaderboard?.length > 0;

  const tableHeader = (
    <LeaderboardTableHeader
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      orderBy={orderBy}
      setOrderBy={setOrderBy}
    />
  );

  const filtersPanel = (
    <LeaderboardFilters
      categories={categories}
      timePeriods={timePeriods}
      selectedTimeFilter={selectedTimeFilter}
      setSelectedTimeFilter={setSelectedTimeFilter}
      selectedCategory={selectedCategory}
      setSelectedCategory={setSelectedCategory}
    />
  );

  const title = (
    <Typography
      variant="h4"
      sx={{
        fontSize: { xs: '1.875rem', sm: '2.5rem' },
        fontWeight: 700,
        mb: 1.5,
        color: 'text.primary',
      }}
    >
      {t('leaderboard.title')}
    </Typography>
  );

  return (
    <ComponentErrorBoundary componentName="Leaderboard">
      <Box>
        {title}
        {filtersPanel}
        <Box sx={{ width: '100%', overflowX: 'auto', display: { xs: 'none', sm: 'block' } }}>
          <Box sx={{ minWidth: 600 }}>
            <Table>
              {tableHeader}
              <TableBody>
                {isLoading ? (
                  <LeaderboardTableSkeleton rows={10} />
                ) : hasData ? (
                  renderedEntriesList
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: 'text.secondary' }}>
                        {t('leaderboard.noTradersFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Mobile: a proper search bar + sort dropdown replace the cramped
            table header, and each trader renders as its own uniformly-styled
            card row inside a single rounded, bordered container. These stay
            mounted during loading — only the list below swaps for skeletons —
            so filtering/searching doesn't hide the controls that triggered it. */}
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <LeaderboardToolbar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
          />
        </Box>
        <Box
          sx={{
            display: { xs: 'block', sm: 'none' },
            borderRadius: 3,
            maxHeight: 'calc(100vh - 320px)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: '4px' },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          }}
        >
          {isLoading ? (
            Array.from({ length: 8 }).map((_, i) => <LeaderboardMobileCardSkeleton key={i} />)
          ) : hasData ? (
            filteredLeaderboard.map((entry, index) => (
              <LeaderboardMobileCard
                key={entry.id || index}
                entry={entry}
                index={index}
                orderBy={orderBy}
              />
            ))
          ) : (
            <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
              {t('leaderboard.noTradersFound')}
            </Typography>
          )}
        </Box>

        {!isLoading && hasData && hasNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
            <Button
              variant="text"
              onClick={onLoadMore}
              disabled={isFetchingNextPage}
              endIcon={!isFetchingNextPage && <KeyboardArrowDownIcon />}
              sx={{
                px: 3,
                py: 1,
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                color: 'text.primary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {isFetchingNextPage ? t('common.loading') : t('portfolio.loadMore')}
            </Button>
          </Box>
        )}
      </Box>
    </ComponentErrorBoundary>
  );
};
